import { useNavigate } from 'react-router-dom';
import { useInstantSearch, useHits } from 'react-instantsearch';
import { Circuitry, Package, Star, Wine, Check, X, Clock, MagnifyingGlass } from '@phosphor-icons/react';
import { useStore } from '../../context/StoreContext';
import { useCart } from '../../context/CartContext';
import { getCategoryIcon } from '../../utils/categoryIcons';
import { insights } from '../../services/algoliaInsights';
import { recordEvent } from '../../utils/userProfile';
import { AddToCartStepper } from '../Product/AddToCartStepper';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

/** Keeps availability chips inside the card when labels are long */
const PILL_WRAP = {
  maxWidth: '100%',
  boxSizing: 'border-box',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  lineHeight: 1.25,
  minWidth: 0,
};

// Product Hit Components
export const ProductHit = ({ hit, showFullDetails = false, sendEvent }) => {
  const { t } = useTranslation();
  const { selectedStore } = useStore();
  const { setIsCartOpen } = useCart();
  const { results, use } = useInstantSearch();
  const navigate = useNavigate();
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const storeData = hit.store_data?.[`store_${selectedStore?.padStart(3, '0')}`];
  const price = storeData?.price || hit.price;
  const format = hit.attributes?.format || '750 ml';
  const country = hit.attributes?.pays_origine || '';
  const wineType = hit.color || '';
  const saqCode = hit.sku || hit.objectID;
  const rating = hit.customer_rating;
  const productUrl = `/produit/${hit.objectID}`;

  const handleImageClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImageModalOpen(true);
  };

  const handleModalClose = () => {
    setImageModalOpen(false);
  };

  const handleProductClick = () => {
    // Get the current index name dynamically
    const currentIndexName = use?.indexName || 'saq_products';
    
    // Track click event using InstantSearch's sendEvent
    if (sendEvent) {
      try {
        sendEvent('click', hit, 'Product Click');
      } catch (error) {
        console.error('ProductHit sendEvent error:', error);
      }
    } else {
      // Fallback to manual insights with dynamic index name
      const queryID = results?.queryID;
      const position = hit.__position;
      
      if (queryID && position !== undefined) {
        insights.clickedObjectIDsAfterSearch(
          'Product Click',
          currentIndexName,
          [hit.objectID],
          [position],
          queryID
        );
      } else {
        insights.clickedObjectIDs(
          'Product Click',
          currentIndexName,
          [hit.objectID]
        );
      }
    }

    recordEvent({
      type: 'click',
      product: { ...hit, selectedStore: String(selectedStore || '1') },
    });

    navigate(productUrl);
  };

  return (
    <>
      <div
        className="product-card"
        style={{
          display: 'flex',
          flexDirection: 'column',
          background: '#fff',
          borderRadius: 18,
          boxShadow: '0 4px 24px rgba(75,22,76,0.10)',
          padding: 24,
          margin: 0,
          transition: 'box-shadow 0.2s',
          cursor: 'pointer',
          width: '100%',
          maxWidth: '100%',
          height: '100%',
          minHeight: 0,
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
        onClick={handleProductClick}
        onMouseOver={e => e.currentTarget.style.boxShadow = '0 8px 32px rgba(75,22,76,0.16)'}
        onMouseOut={e => e.currentTarget.style.boxShadow = '0 4px 24px rgba(75,22,76,0.10)'}
      >
        <div
          className="product-card-main"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            minWidth: 0,
          }}
        >
        {/* Main Content: Image + Details — grows so footer stays at card bottom */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: 24,
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            width: '100%',
          }}
        >
          <div className="product-image-container" style={{ minWidth: 120, maxWidth: 140, width: 140, flexShrink: 0, alignSelf: 'flex-start', height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            {hit.image ? (
              <div style={{ position: 'relative' }}>
                <img 
                  src={hit.image.startsWith('http') ? hit.image : `https:${hit.image}`} 
                  alt={hit.name} 
                  style={{ 
                    width: 116, 
                    height: 180, 
                    objectFit: 'contain', 
                    borderRadius: 12, 
                    background: '#f8f6fa', 
                    boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
                    cursor: 'pointer'
                  }}
                  onClick={handleImageClick}
                  onMouseOver={e => {
                    e.target.style.transform = 'scale(1.02)';
                    e.target.style.transition = 'transform 0.2s ease';
                  }}
                  onMouseOut={e => {
                    e.target.style.transform = 'scale(1)';
                  }}
                />
                {/* Zoom icon overlay */}
                <div 
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: 'rgba(75,22,76,0.8)',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.2s ease',
                    pointerEvents: 'none'
                  }}
                  className="zoom-icon"
                >
                  <MagnifyingGlass size={16} color="#fff" weight="bold" />
                </div>
              </div>
            ) : (
              <Wine size={64} color="#4B164C" />
            )}
          </div>
          
          <div
            className="product-item-content-container"
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              justifyContent: 'flex-start',
            }}
          >
            {/* Top Section: Product Info */}
            <div className="product-item-details" style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
              <a href={productUrl} onClick={e => { e.stopPropagation(); navigate(productUrl); }} className="product-item-link" style={{ textDecoration: 'none' }}>
                <strong className="product name product-item-name" style={{ fontSize: 20, color: '#4B164C', lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{hit.name}</strong>
              </a>
              <div className="product product-item-identity-format" style={{ color: '#888', fontSize: 15 }}>
                {wineType}  |  <span className="format">{format}</span>  |  {country}
              </div>
              <div className="saq-code" style={{ color: '#bbb', fontSize: 13 }}>Code SAQ {saqCode}</div>
              {rating && (
                <div className="product-reviews-summary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Star size={16} weight="fill" color="#4B164C" />
                  <span style={{ color: '#4B164C', fontWeight: 600 }}>{rating}</span>
                </div>
              )}
            </div>

            {/* Middle Section: Price */}
            <div className="price-box price-final_price" style={{ flexShrink: 0 }}>
              <span className="price" style={{ fontSize: 24, fontWeight: 700, color: '#4B164C' }}>{price ? `${price.toLocaleString('fr-CA', { minimumFractionDigits: 2 })} $` : ''}</span>
            </div>

            {/* Availability — wraps inside card */}
            <div
              className="availability-container"
              style={{
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
              }}
            >
              {/* Parse availability_front into separate pills */}
              {hit.attributes?.availability_front ? (
                (() => {
                  // Handle both string and array formats
                  const availabilityArray = Array.isArray(hit.attributes.availability_front) 
                    ? hit.attributes.availability_front 
                    : hit.attributes.availability_front.split(/(?=[A-Z])/).filter(part => part.trim());
                  
                  // Check if "Disponible bientôt" is present
                  const hasSoonAvailable = availabilityArray.some(type => 
                    type.toLowerCase().includes('bientôt') || type.toLowerCase().includes('soon')
                  );
                  
                  // If "Disponible bientôt" is present, only show that
                  if (hasSoonAvailable) {
                    const soonType = availabilityArray.find(type => 
                      type.toLowerCase().includes('bientôt') || type.toLowerCase().includes('soon')
                    );
                    return (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        background: '#fff3e0',
                        color: '#f57c00',
                        padding: '4px 10px',
                        borderRadius: 16,
                        fontSize: 11,
                        fontWeight: 600,
                        border: '1px solid #ffcc80',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        ...PILL_WRAP,
                      }}>
                        <Clock size={12} weight="bold" style={{ flexShrink: 0 }} />
                        <span style={{ minWidth: 0 }}>{soonType.trim()}</span>
                      </div>
                    );
                  }
                  
                  // Otherwise show all availability types
                  return availabilityArray.map((availabilityType, index) => {
                    const isSoonAvailable = availabilityType.toLowerCase().includes('bientôt') || availabilityType.toLowerCase().includes('soon');
                    
                    return (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        background: isSoonAvailable ? '#fff3e0' : '#e8f5e8',
                        color: isSoonAvailable ? '#f57c00' : '#388e3c',
                        padding: '4px 10px',
                        borderRadius: 16,
                        fontSize: 11,
                        fontWeight: 600,
                        border: `1px solid ${isSoonAvailable ? '#ffcc80' : '#c8e6c9'}`,
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        ...PILL_WRAP,
                      }}>
                        {isSoonAvailable ? (
                          <Clock size={12} weight="bold" style={{ flexShrink: 0 }} />
                        ) : (
                          <Check size={12} weight="bold" style={{ flexShrink: 0 }} />
                        )}
                        <span style={{ minWidth: 0 }}>{availabilityType.trim()}</span>
                      </div>
                    );
                  });
                })()
              ) : null}
              
              {/* Online availability pill - show if "En ligne" is not in availability_front AND no "Disponible bientôt" */}
              {(() => {
                const availabilityArray = Array.isArray(hit.attributes?.availability_front) 
                  ? hit.attributes.availability_front 
                  : hit.attributes?.availability_front?.split(/(?=[A-Z])/).filter(part => part.trim()) || [];
                
                const hasSoonAvailable = availabilityArray.some(type => 
                  type.toLowerCase().includes('bientôt') || type.toLowerCase().includes('soon')
                );
                
                if (hasSoonAvailable) return null; // Don't show other pills if "Disponible bientôt"
                
                const hasOnlineAvailability = availabilityArray.some(type => 
                  type.toLowerCase().includes('ligne') || type.toLowerCase().includes('online')
                );
                
                if (!hasOnlineAvailability) {
                  return (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      background: '#ffebee',
                      color: '#d32f2f',
                      padding: '4px 10px',
                      borderRadius: 16,
                      fontSize: 11,
                      fontWeight: 600,
                      border: '1px solid #ffcdd2',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      ...PILL_WRAP,
                    }}>
                      <X size={12} weight="bold" style={{ flexShrink: 0 }} />
                      <span style={{ minWidth: 0 }}>Non en ligne</span>
                    </div>
                  );
                }
                return null;
              })()}
              
              {/* Store availability pill - show if "En succursale" is not in availability_front AND no "Disponible bientôt" */}
              {(() => {
                const availabilityArray = Array.isArray(hit.attributes?.availability_front) 
                  ? hit.attributes.availability_front 
                  : hit.attributes?.availability_front?.split(/(?=[A-Z])/).filter(part => part.trim()) || [];
                
                const hasSoonAvailable = availabilityArray.some(type => 
                  type.toLowerCase().includes('bientôt') || type.toLowerCase().includes('soon')
                );
                
                if (hasSoonAvailable) return null; // Don't show other pills if "Disponible bientôt"
                
                const hasStoreAvailability = availabilityArray.some(type => 
                  type.toLowerCase().includes('succursale') || type.toLowerCase().includes('store')
                );
                
                if (!hasStoreAvailability) {
                  return (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      background: '#ffebee',
                      color: '#d32f2f',
                      padding: '4px 10px',
                      borderRadius: 16,
                      fontSize: 11,
                      fontWeight: 600,
                      border: '1px solid #ffcdd2',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      ...PILL_WRAP,
                    }}>
                      <X size={12} weight="bold" style={{ flexShrink: 0 }} />
                      <span style={{ minWidth: 0 }}>En magasin</span>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>
        </div>
        {/* Footer: always at bottom of card (grid rows stretch) */}
        <div
          className="product-item-actions"
          style={{
            marginTop: 'auto',
            paddingTop: 16,
            borderTop: '1px solid #ece8ed',
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
            flexShrink: 0,
            boxSizing: 'border-box',
          }}
        >
          <div onClick={e => e.stopPropagation()}>
            <AddToCartStepper
              product={hit}
              queryID={results?.queryID ?? null}
              compact
              compactShowLabel
              onAdd={() => setIsCartOpen?.(true)}
            />
          </div>
          <a
            href={productUrl}
            onClick={e => {
              e.stopPropagation();
              navigate(productUrl);
            }}
            className="action secondary product-card-view-link"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              background: 'transparent',
              color: '#6b4a6d',
              border: 'none',
              padding: '6px 4px',
              fontWeight: 600,
              fontSize: 14,
              textDecoration: 'underline',
              textUnderlineOffset: 3,
              textDecorationThickness: '1px',
              boxShadow: 'none',
              transition: 'color 0.2s',
            }}
            onMouseOver={e => {
              e.currentTarget.style.color = '#4B164C';
            }}
            onMouseOut={e => {
              e.currentTarget.style.color = '#6b4a6d';
            }}
          >
            {t('view_product')}
          </a>
        </div>
      </div>

      {/* Image Modal */}
      {imageModalOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            cursor: 'pointer'
          }}
          onClick={handleModalClose}
        >
          <div 
            style={{
              position: 'relative',
              maxWidth: '90%',
              maxHeight: '90%',
              background: '#fff',
              borderRadius: 16,
              padding: 24,
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={handleModalClose}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                background: 'rgba(0,0,0,0.5)',
                border: 'none',
                borderRadius: '50%',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#fff',
                fontSize: 18,
                zIndex: 1
              }}
            >
              ×
            </button>
            {hit.image ? (
              <img 
                src={hit.image.startsWith('http') ? hit.image : `https:${hit.image}`} 
                alt={hit.name} 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '70vh', 
                  objectFit: 'contain',
                  borderRadius: 8
                }}
              />
            ) : (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                width: 300, 
                height: 400,
                background: '#f8f6fa',
                borderRadius: 8
              }}>
                <Wine size={120} color="#4B164C" />
              </div>
            )}
            <div style={{ 
              marginTop: 16, 
              textAlign: 'center',
              color: '#4B164C',
              fontWeight: 600,
              fontSize: 16
            }}>
              {hit.name}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .product-image-container:hover .zoom-icon {
          opacity: 1 !important;
        }
      `}</style>
    </>
  );
};

// Dropdown Hit Component for search suggestions
export const DropdownProductHit = ({ hit, minimalDropdown, sendEvent }) => {
  const { selectedStore } = useStore();
  const { setIsCartOpen } = useCart();
  const { results, use } = useInstantSearch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const IconComponent = getCategoryIcon(hit.categorias_flat);
  
  const storeData = hit.store_data?.[`store_${(selectedStore || '1').toString().padStart(3, '0')}`];
  const price = storeData?.price || hit.price;

  const handleClick = () => {
    // Get the current index name dynamically
    const currentIndexName = use?.indexName || 'saq_products';
    
    // Track click event using InstantSearch's sendEvent
    if (sendEvent) {
      sendEvent('click', hit, 'Product Click');
    } else {
      // Fallback to manual insights with dynamic index name
      const queryID = results?.queryID;
      const position = hit.__position;
      
      if (queryID) {
        insights.clickedObjectIDsAfterSearch(
          'Product Click',
          currentIndexName,
          [hit.objectID],
          [position],
          queryID
        );
      } else {
        insights.clickedObjectIDs(
          'Product Click',
          currentIndexName,
          [hit.objectID]
        );
      }
    }

    recordEvent({
      type: 'click',
      product: { ...hit, selectedStore: String(selectedStore || '1') },
    });

    navigate(`/produit/${hit.objectID}`);
  };

  return (
    <div
      className="dropdown-item"
      onMouseDown={handleClick}
      style={{
        borderRadius: 14,
        boxShadow: '0 2px 8px rgba(75,22,76,0.08)',
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        padding: '18px 12px',
        margin: '0 4px',
        minWidth: 120,
        maxWidth: 180,
        cursor: 'pointer',
        border: 'none',
        transition: 'box-shadow 0.2s',
      }}
      onMouseOver={e => e.currentTarget.style.boxShadow = '0 8px 24px rgba(75,22,76,0.12)'}
      onMouseOut={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(75,22,76,0.08)'}
    >
      <div className="product-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 60, height: 60, marginBottom: 10 }}>
        {hit.image ? (
          <img src={hit.image.startsWith('http') ? hit.image : `https:${hit.image}`} alt={hit.name} style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 10, background: '#faf9f7', boxShadow: '0 1px 4px rgba(0,0,0,0.03)' }} />
        ) : (
          <Wine size={36} color="#4B164C" />
        )}
      </div>
      <h3 style={{ color: '#4B164C', fontWeight: 700, fontSize: 16, textAlign: 'center', margin: '0 0 6px', lineHeight: 1.2 }}>{hit.name}</h3>
      <div style={{ fontSize: 14, color: '#888', fontWeight: 500, marginBottom: 8, textAlign: 'center' }}>
        {t('price')}: ${hit.price?.toLocaleString('fr-CA', { minimumFractionDigits: 2 })} CAD
      </div>
      <div onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
        <AddToCartStepper
          product={hit}
          queryID={results?.queryID ?? null}
          compact
          onAdd={() => setIsCartOpen?.(true)}
        />
      </div>
    </div>
  );
};

// In HitsSection, pass minimalDropdown prop if provided
export const HitsSection = ({ title, query, minimalDropdown }) => {
  const { items } = useHits();
  return (
    <div>
      {items.map((hit, idx) => (
        <DropdownProductHit key={hit.objectID || idx} hit={hit} minimalDropdown={minimalDropdown} />
      ))}
    </div>
  );
}; 